import { useState } from 'react';
import { GoogleLogin } from '@react-oauth/google';
import { Loader2, MailCheck, ShieldCheck } from 'lucide-react';
import { api, ApiError, setToken } from '@/lib/api';
import { googleAvailable } from '@/lib/google';

/**
 * "Confirm it's you" before a sensitive action (the lease, ID or bank details).
 * A long-lived session keeps the resident signed in, but these actions need a
 * real sign-in from the last half hour: with Google (same account as the email
 * on file), or a fresh link to that email which returns to `next`.
 */
export function ConfirmIdentity({
  next,
  reason,
  onConfirmed,
}: {
  /** Internal path to return to after confirming by email (e.g. "/lease"). */
  next: string;
  /** One line on why, e.g. "כדי לראות את החוזה". */
  reason: string;
  onConfirmed: () => void;
}) {
  const [state, setState] = useState<'idle' | 'busy' | 'sent'>('idle');
  const [error, setError] = useState('');

  async function viaEmail() {
    setState('busy');
    setError('');
    try {
      await api.confirmEmail(next);
      setState('sent');
    } catch (e) {
      setState('idle');
      setError(
        e instanceof ApiError && e.code === 'RESIDENT_NO_EMAIL'
          ? 'אין כתובת מייל רשומה. פנו לצוות הדיור.'
          : 'השליחה נכשלה. נסו שוב בעוד רגע.',
      );
    }
  }

  return (
    <div
      className="rounded-2xl border border-brand-100 bg-white p-5 text-center shadow-sm"
      role="dialog"
      aria-labelledby="confirm-title"
    >
      <ShieldCheck className="mx-auto mb-2 h-8 w-8 text-brand-500" />
      <h2 id="confirm-title" className="text-lg font-bold text-brand-900">
        אימות קצר שזה אתם
      </h2>
      <p className="mt-1 text-[15px] leading-relaxed text-slate-600">{reason}, צריך לוודא שזה אתם. זה לוקח רגע.</p>

      {state === 'sent' ? (
        <p
          className="mt-4 flex items-start gap-2 rounded-xl bg-brand-50 p-3.5 text-start text-[15px] leading-relaxed text-brand-900"
          role="status"
        >
          <MailCheck className="mt-0.5 h-5 w-5 shrink-0 text-brand-500" />
          שלחנו קישור אימות למייל שלכם. פתחו אותו בטלפון הזה, והוא יחזיר אתכם לכאן.
        </p>
      ) : (
        <>
          {googleAvailable() && (
            <div className="mt-4 flex justify-center">
              <GoogleLogin
                onSuccess={async (res) => {
                  if (!res.credential) return setError('האימות עם Google לא הושלם. נסו שוב.');
                  setState('busy');
                  setError('');
                  try {
                    const { accessToken } = await api.confirmGoogle(res.credential);
                    setToken(accessToken);
                    onConfirmed();
                  } catch (e) {
                    setState('idle');
                    setError(
                      e instanceof ApiError && e.code === 'RESIDENT_GOOGLE_MISMATCH'
                        ? 'חשבון ה-Google הזה שונה מהמייל הרשום אצל צוות הדיור. אמתו עם קישור למייל.'
                        : 'האימות עם Google לא הושלם. נסו שוב או קבלו קישור למייל.',
                    );
                  }
                }}
                onError={() => setError('האימות עם Google לא הושלם. נסו שוב או קבלו קישור למייל.')}
                text="continue_with"
                shape="pill"
                width={280}
              />
            </div>
          )}
          <button
            type="button"
            onClick={() => void viaEmail()}
            disabled={state === 'busy'}
            className="mt-3 inline-flex min-h-[48px] w-full items-center justify-center gap-2 rounded-xl border border-brand-200 px-4 text-[16px] font-semibold text-brand-700 disabled:opacity-60"
          >
            {state === 'busy' && <Loader2 className="h-4 w-4 animate-spin" />}
            שליחת קישור אימות למייל
          </button>
        </>
      )}
      {error && (
        <p className="mt-3 text-start text-[15px] leading-relaxed text-red-600" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
